import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { callApi } from "../../api/api";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileText,
  Filter,
  Image as ImageIcon,
  LayoutGrid,
  List,
  Mail,
  Pencil,
  RefreshCw,
  Search,
  User,
  ExternalLink,
  ArrowRightLeft,
  X,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

import AssignmentMetadataPanel from "./AssignmentMetadataPanel";

const THREAD_PAGE_SIZE = 10;
const EMPTY_ATTACHMENTS = [];

function scrollToVisible(target, behavior = "smooth") {
  if (!target) return;

  const nestedScroller = target.closest("[data-thread-detail]");
  if (nestedScroller && nestedScroller !== target) {
    const nestedTop =
      target.getBoundingClientRect().top -
      nestedScroller.getBoundingClientRect().top +
      nestedScroller.scrollTop;
    nestedScroller.scrollTo({ top: Math.max(0, nestedTop - 8), behavior });
  }

  const scrollParent = target.closest(".app-shell__content");
  if (scrollParent) {
    const top =
      target.getBoundingClientRect().top -
      scrollParent.getBoundingClientRect().top +
      scrollParent.scrollTop;
    scrollParent.scrollTo({ top: Math.max(0, top - 8), behavior });
    return;
  }

  target.scrollIntoView({ block: "start", behavior });
}

function isVerifiedEmail(email) {
  return email?.verified_flag === true || email?.verified_flag === 1;
}

function formatListDate(date) {
  if (!date) return "—";
  return new Date(date).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatFileSize(bytes) {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function fileKind(file) {
  const name = String(file?.name || file?.path || "").toLowerCase();
  const type = String(file?.content_type || "").toLowerCase();
  if (type.includes("pdf") || name.endsWith(".pdf")) return "pdf";
  if (
    type.startsWith("image/") ||
    /\.(png|jpe?g|gif|webp|tif{1,2}|heic|heif)$/.test(name)
  ) {
    return "image";
  }
  return "file";
}

function formatEntryDate(date) {
  if (!date) return "—";
  return new Date(date).toLocaleString();
}

function fitPreviewIframe(iframe) {
  try {
    const doc = iframe?.contentDocument;
    if (!doc || !doc.body) return;

    if (!doc.getElementById("cfp-preview-scroll")) {
      const style = doc.createElement("style");
      style.id = "cfp-preview-scroll";
      style.textContent = `
        html, body {
          overflow: auto !important;
          margin: 0 !important;
          padding: 8px 12px !important;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
          color: #1e293b !important;
          background: #ffffff !important;
          -webkit-font-smoothing: antialiased;
        }
        .email-header-card {
          background: #f8fafc !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 10px !important;
          padding: 10px 12px !important;
          margin-bottom: 10px !important;
          box-shadow: 0 1px 2px rgba(15,23,42,0.03) !important;
        }
        .email-header-top {
          display: flex !important;
          align-items: flex-start !important;
          gap: 10px !important;
          margin-bottom: 8px !important;
        }
        .email-avatar {
          width: 32px !important;
          height: 32px !important;
          border-radius: 50% !important;
          background: linear-gradient(135deg, #4f46e5 0%, #3730a3 100%) !important;
          color: #ffffff !important;
          font-size: 12px !important;
          font-weight: 700 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          flex-shrink: 0 !important;
          box-shadow: 0 2px 6px rgba(79,70,229,0.2) !important;
        }
        .email-header-title {
          font-size: 14px !important;
          font-weight: 700 !important;
          color: #0f172a !important;
          line-height: 1.35 !important;
          margin: 0 !important;
        }
        .email-meta-grid {
          display: grid !important;
          grid-template-columns: 50px 1fr !important;
          gap: 6px 10px !important;
          font-size: 12.5px !important;
          align-items: center !important;
          padding-top: 10px !important;
          border-top: 1px solid #edf2f7 !important;
        }
        .email-meta-label {
          color: #64748b !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          font-size: 10px !important;
          letter-spacing: 0.04em !important;
        }
        .email-meta-value {
          color: #1e293b !important;
          font-weight: 500 !important;
          word-break: break-all !important;
        }
        .email-meta-email {
          color: #64748b !important;
          font-size: 12px !important;
          margin-left: 4px !important;
        }
        .email-body-content {
          padding: 8px 4px !important;
          font-size: 14px !important;
          line-height: 1.7 !important;
          color: #334155 !important;
        }
        .email-body-content pre {
          font-family: inherit !important;
          font-size: 14px !important;
          line-height: 1.7 !important;
          color: #334155 !important;
          white-space: pre-wrap !important;
          word-break: break-word !important;
          margin: 0 !important;
        }
        hr {
          border: none !important;
          border-top: 1px solid #e2e8f0 !important;
          margin: 14px 0 !important;
        }
        p, div, span, td, th {
          font-family: inherit !important;
        }
      `;
      (doc.head || doc.documentElement).appendChild(style);
    }

    // Transform unstyled plain text email headers into a modern email card
    if (!doc.querySelector(".email-header-card")) {
      const rawText = doc.body.innerText || doc.body.textContent || "";
      if (/Subject:\s*[^\n\r]+/i.test(rawText) && /From:\s*[^\n\r]+/i.test(rawText)) {
        const esc = (t) => String(t || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

        const subjectMatch = rawText.match(/Subject:\s*([^\n\r]+)/i);
        const fromMatch = rawText.match(/From:\s*([^\n\r]+)/i);
        const toMatch = rawText.match(/To:\s*([^\n\r]+)/i);
        const ccMatch = rawText.match(/CC:\s*([^\n\r]+)/i);

        const fromRaw = fromMatch ? fromMatch[1].trim() : "";
        const fromClean = fromRaw.replace(/^"+|"+$/g, "");
        const initials = fromClean
          .split(/[\s@]+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((s) => s[0]?.toUpperCase())
          .join("") || "M";

        let cleanBody = rawText
          .replace(/Subject:\s*[^\n\r]+(\r?\n)?/i, "")
          .replace(/From:\s*[^\n\r]+(\r?\n)?/i, "")
          .replace(/To:\s*[^\n\r]+(\r?\n)?/i, "")
          .replace(/CC:\s*[^\n\r]+(\r?\n)?/i, "")
          .trim();

        doc.body.innerHTML = `
          <div class="email-header-card">
            <div class="email-header-top">
              <div class="email-avatar">${esc(initials)}</div>
              <div style="flex: 1; min-width: 0;">
                ${subjectMatch ? `<h3 class="email-header-title">${esc(subjectMatch[1].trim())}</h3>` : ""}
              </div>
            </div>
            <div class="email-meta-grid">
              ${fromMatch ? `<span class="email-meta-label">From:</span><span class="email-meta-value">${esc(fromMatch[1].trim())}</span>` : ""}
              ${toMatch ? `<span class="email-meta-label">To:</span><span class="email-meta-value">${esc(toMatch[1].trim())}</span>` : ""}
              ${ccMatch ? `<span class="email-meta-label">CC:</span><span class="email-meta-value">${esc(ccMatch[1].trim())}</span>` : ""}
            </div>
          </div>
          <div class="email-body-content">
            <pre>${esc(cleanBody)}</pre>
          </div>
        `;
      }
    }

    const height = Math.max(
      doc.documentElement?.scrollHeight || 0,
      doc.body?.scrollHeight || 0,
    );
    if (height > 0) {
      iframe.style.height = `${height}px`;
    }
  } catch {
    // Cross-origin previews keep native iframe scrolling.
  }
}

function fileIconColors(kind) {
  if (kind === "pdf") {
    return { background: "#fee2e2", color: "#dc2626" };
  }
  if (kind === "image") {
    return { background: "#dcfce7", color: "#16a34a" };
  }
  return { background: "#dbeafe", color: "#2563eb" };
}


function searchableEmailText(email) {
  if (!email) return "";
  return [
    email.email_subject,
    email.mail_from_address,
    email.mail_from_name,
    email.sender_name,
    email.email_body,
    email.body,
    email.body_preview,
    email.snippet,
    email.email_content,
    email.content,
    email.text_body,
  ]
    .filter((value) => value != null && String(value).trim() !== "")
    .join(" ")
    .toLowerCase();
}

function emailMatchesSearch(email, query) {
  const needle = String(query || "").trim().toLowerCase();
  if (!needle) return true;
  return searchableEmailText(email).includes(needle);
}

const DEFAULT_THREAD_FILTERS = {
  status: "all",
  attachments: "any",
  datePreset: "any",
  dateFrom: "",
  dateTo: "",
  sender: "",
};

function startOfLocalDay(value) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function endOfLocalDay(value) {
  const date = new Date(value);
  date.setHours(23, 59, 59, 999);
  return date.getTime();
}

function emailMatchesThreadFilters(email, filters) {
  if (filters.status === "review" && isVerifiedEmail(email)) return false;

  const attachmentCount = (email.attachments || []).length;
  if (filters.attachments === "has" && attachmentCount === 0) return false;
  if (filters.attachments === "none" && attachmentCount > 0) return false;

  const senderNeedle = String(filters.sender || "").trim().toLowerCase();
  if (senderNeedle) {
    const senderText = `${email.mail_from_address || ""} ${email.mail_from_name || ""}`.toLowerCase();
    if (!senderText.includes(senderNeedle)) return false;
  }

  if (filters.datePreset && filters.datePreset !== "any") {
    const timestamp = new Date(email.created_at).getTime();
    if (!Number.isFinite(timestamp)) return false;
    const now = Date.now();
    if (filters.datePreset === "today") {
      if (timestamp < startOfLocalDay(now) || timestamp > endOfLocalDay(now)) {
        return false;
      }
    } else if (filters.datePreset === "7d") {
      if (timestamp < now - 7 * 24 * 60 * 60 * 1000) return false;
    } else if (filters.datePreset === "30d") {
      if (timestamp < now - 30 * 24 * 60 * 60 * 1000) return false;
    } else if (filters.datePreset === "custom") {
      if (filters.dateFrom && timestamp < startOfLocalDay(filters.dateFrom)) {
        return false;
      }
      if (filters.dateTo && timestamp > endOfLocalDay(filters.dateTo)) {
        return false;
      }
    }
  }

  return true;
}

function hasActiveThreadFilters(filters) {
  return (
    filters.status !== "all" ||
    filters.attachments !== "any" ||
    filters.datePreset !== "any" ||
    Boolean(String(filters.dateFrom || "").trim()) ||
    Boolean(String(filters.dateTo || "").trim()) ||
    Boolean(String(filters.sender || "").trim())
  );
}

function FilterChip({ active, children, onClick, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={ariaLabel || (typeof children === "string" ? children : undefined)}
      style={{
        ...styles.filterChip,
        ...(active ? styles.filterChipActive : {}),
      }}
    >
      {children}
    </button>
  );
}

function ThreadAdvancedFilters({
  open,
  anchorRef,
  draft,
  setDraft,
  onApply,
  onClear,
  onClose,
}) {
  const popoverRef = useRef(null);
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        right: Math.max(12, window.innerWidth - rect.right),
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (anchorRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      onClose();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, anchorRef, onClose]);

  if (!open || !position) return null;

  const setField = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Advanced filters"
      style={{
        ...styles.filtersPopover,
        top: position.top,
        right: position.right,
      }}
    >
      <div style={styles.filtersTitle}>Advanced filters</div>

      <div style={styles.filtersSection}>
        <div style={styles.filtersSectionLabel}>STATUS</div>
        <div style={styles.filterChipRow}>
          <FilterChip
            active={draft.status === "all"}
            onClick={() => setField("status", "all")}
          >
            All
          </FilterChip>
          <FilterChip
            active={draft.status === "review"}
            onClick={() => setField("status", "review")}
          >
            Pending
          </FilterChip>
        </div>
      </div>

      <div style={styles.filtersSection}>
        <div style={styles.filtersSectionLabel}>ATTACHMENTS</div>
        <div style={styles.filterChipRow}>
          <FilterChip
            active={draft.attachments === "any"}
            onClick={() => setField("attachments", "any")}
          >
            Any
          </FilterChip>
          <FilterChip
            active={draft.attachments === "has"}
            onClick={() => setField("attachments", "has")}
          >
            Has attachments
          </FilterChip>
          <FilterChip
            active={draft.attachments === "none"}
            onClick={() => setField("attachments", "none")}
          >
            No attachments
          </FilterChip>
        </div>
      </div>

      <div style={styles.filtersSection}>
        <div style={styles.filtersSectionLabel}>DATE RECEIVED</div>
        {[
          ["any", "Any time"],
          ["today", "Today"],
          ["7d", "Last 7 days"],
          ["30d", "Last 30 days"],
          ["custom", "Custom date range"],
        ].map(([value, label]) => (
          <label key={value} style={styles.filtersRadio}>
            <input
              type="radio"
              name="v2-thread-date-preset"
              checked={draft.datePreset === value}
              onChange={() => setField("datePreset", value)}
            />
            {label}
          </label>
        ))}
        {draft.datePreset === "custom" ? (
          <div style={styles.filtersDateRow}>
            <input
              type="date"
              aria-label="From date"
              value={draft.dateFrom}
              onChange={(event) => setField("dateFrom", event.target.value)}
              style={styles.filtersDateInput}
            />
            <input
              type="date"
              aria-label="To date"
              value={draft.dateTo}
              onChange={(event) => setField("dateTo", event.target.value)}
              style={styles.filtersDateInput}
            />
          </div>
        ) : null}
      </div>

      <div style={styles.filtersSection}>
        <div style={styles.filtersSectionLabel}>SENDER</div>
        <div style={styles.threadSearchWrap}>
          <Search size={14} style={styles.threadSearchIcon} />
          <input
            type="search"
            value={draft.sender}
            onChange={(event) => setField("sender", event.target.value)}
            placeholder="Search sender..."
            aria-label="Search sender"
            style={styles.threadSearchInput}
          />
        </div>
      </div>

      <div style={styles.filtersFooter}>
        <button type="button" style={styles.filtersClear} onClick={onClear}>
          Clear filters
        </button>
        <button type="button" style={styles.filtersApply} onClick={onApply}>
          Apply
        </button>
      </div>
    </div>,
    document.body,
  );
}

export default function AssignmentThreadView({
  workItem,
  thread,
  activeTab,
  selectedMsgId,
  selectedRow,
  reviewMode,
  isNarrow,
  previewLoading,
  previewUrl,
  previewKind,
  previewError,
  onSelectEmail,
  onCollapse,
  onEdit,
  onReindex,
  onPreview,
  onOpenAll,
  onDownload,
  onCloseReview,
  onTransferSuccess,
  onSaved,
}) {
  const navigate = useNavigate();
  const [sortOrder, setSortOrder] = useState("newest");
  const [listPage, setListPage] = useState(1);
  const [threadSearch, setThreadSearch] = useState("");
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_THREAD_FILTERS);
  const [draftFilters, setDraftFilters] = useState(DEFAULT_THREAD_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [detailTab, setDetailTab] = useState("preview");
  const [attachLayout, setAttachLayout] = useState("grid");
  const [previewScrollNonce, setPreviewScrollNonce] = useState(0);
  const [previewedPath, setPreviewedPath] = useState(null);
  const previewFrameRef = useRef(null);
  const detailRef = useRef(null);
  const filtersBtnRef = useRef(null);
  const [localOverrides, setLocalOverrides] = useState({});
  const { instance, accounts } = useMsal();
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [selectedTargetDept, setSelectedTargetDept] = useState("");
  const [transferRemarks, setTransferRemarks] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [transferError, setTransferError] = useState("");
  const [transferSuccess, setTransferSuccess] = useState("");

  const handleMetadataSaved = useCallback((updatedData) => {
    if (updatedData) {
      setLocalOverrides((prev) => {
        const next = { ...prev };
        (thread?.emails || []).forEach((email) => {
          next[email.msg_id] = {
            ...(next[email.msg_id] || {}),
            ...updatedData,
            msg_id: email.msg_id,
            email_subject: email.email_subject,
            mail_from_address: email.mail_from_address,
            created_at: email.created_at,
            attachments: email.attachments,
          };
        });
        return next;
      });
    }
    onSaved?.(updatedData);
  }, [thread, onSaved]);

  const closeFilters = useCallback(() => setFiltersOpen(false), []);

  const emails = useMemo(() => {
    const list = (thread?.emails || []).map((email) => {
      const override = localOverrides[email.msg_id];
      return override ? { ...email, ...override } : email;
    });
    list.sort((a, b) => {
      const timeA = new Date(a.created_at).getTime();
      const timeB = new Date(b.created_at).getTime();
      return sortOrder === "newest" ? timeB - timeA : timeA - timeB;
    });
    return list;
  }, [thread, sortOrder, localOverrides]);

  const filteredEmails = useMemo(
    () =>
      emails.filter(
        (email) =>
          emailMatchesThreadFilters(email, appliedFilters) &&
          emailMatchesSearch(email, threadSearch),
      ),
    [emails, appliedFilters, threadSearch],
  );

  const totalEmails = emails.length;
  const filteredCount = filteredEmails.length;
  const totalPages = Math.max(1, Math.ceil(filteredCount / THREAD_PAGE_SIZE));
  const showPager = filteredCount > THREAD_PAGE_SIZE;
  const safePage = Math.min(Math.max(1, listPage), totalPages);
  const pagedEmails = filteredEmails.slice(
    (safePage - 1) * THREAD_PAGE_SIZE,
    safePage * THREAD_PAGE_SIZE,
  );
  const filtersActive = hasActiveThreadFilters(appliedFilters);
  const hasQuery = Boolean(threadSearch.trim()) || filtersActive;
  const showThreadSearch = totalEmails > 1;
  const showThreadFilters = totalEmails > 1;

  const selectedEmail =
    emails.find((email) => email.msg_id === selectedMsgId) || emails[0];
  const selectedIndex = emails.findIndex(
    (email) => email.msg_id === selectedEmail?.msg_id,
  );
  const selectedNumber = selectedIndex >= 0 ? selectedIndex + 1 : 0;
  const visibleDetailTab =
    detailTab === "attachments" || detailTab === "metadata"
      ? detailTab
      : "preview";
  const attachments = selectedEmail?.attachments || EMPTY_ATTACHMENTS;
  const previewableAttachments = useMemo(
    () => attachments.filter((file) => file?.path),
    [attachments],
  );

  const currentOverride = selectedEmail?.msg_id ? localOverrides[selectedEmail.msg_id] : null;

  const currentDeptName = useMemo(() => {
    return String(
      selectedEmail?.current_department ||
      selectedEmail?.department ||
      selectedEmail?.department_name ||
      currentOverride?.department ||
      currentOverride?.current_department ||
      "Claims"
    ).trim();
  }, [selectedEmail, currentOverride]);

  useEffect(() => {
    if (showTransferModal) {
      callApi(instance, accounts, "GET", "/api/departments")
        .then((res) => {
          const list = Array.isArray(res?.data?.data)
            ? res.data.data
            : Array.isArray(res?.data)
            ? res.data
            : [];
          setDepartmentsList(list);
        })
        .catch(() => setDepartmentsList([]));
    }
  }, [showTransferModal, instance, accounts]);

  const availableDepartments = useMemo(() => {
    const curr = currentDeptName.toLowerCase();
    return departmentsList.filter((d) => {
      const dName = String(d.departmentName || d.department_name || d.name || d.departmentCode || "").trim().toLowerCase();
      return dName !== curr && dName !== "";
    });
  }, [departmentsList, currentDeptName]);

  const handleExecuteTransfer = async () => {
    if (!selectedTargetDept || transferring) return;
    try {
      setTransferring(true);
      setTransferError("");
      setTransferSuccess("");

      const res = await callApi(instance, accounts, "POST", "/api/departments/transfer", {
        msgId: selectedEmail?.msg_id,
        witsSerialNumber: selectedEmail?.wits_serial_number || selectedEmail?.witsSerialNumber,
        adoUserStoryId: selectedEmail?.ado_user_story_id || selectedEmail?.adoId,
        targetDepartment: selectedTargetDept,
        remarks: transferRemarks || `Queue manually transferred to ${selectedTargetDept}`,
        transferredBy: accounts?.[0]?.name || accounts?.[0]?.username || "User",
      });

      const updatedDept = res?.data?.data?.currentDepartment || selectedTargetDept;
      const updatedAssignee = res?.data?.data?.assignedTo || selectedEmail?.assigned_to;

      if (typeof onSaved === "function") {
        try {
          onSaved({
            ...selectedEmail,
            department: updatedDept,
            current_department: updatedDept,
            assigned_to: updatedAssignee,
            assignedTo: updatedAssignee,
          });
        } catch (_) {}
      }

      setTransferSuccess(`Queue successfully transferred to ${updatedDept}`);
      setTimeout(() => {
        setShowTransferModal(false);
        setTransferSuccess("");
        setSelectedTargetDept("");
        setTransferRemarks("");
        if (typeof onTransferSuccess === "function") {
          onTransferSuccess();
        } else if (typeof onCloseReview === "function") {
          onCloseReview();
        } else if (typeof onCollapse === "function") {
          onCollapse();
        }
        navigate("/request-pool", { replace: true });
      }, 700);
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || "Failed to transfer queue.";
      setTransferError(msg);
    } finally {
      setTransferring(false);
    }
  };

  const assigneeName =
    currentOverride?.assigned_to ||
    currentOverride?.assignedTo ||
    selectedEmail?.assigned_to ||
    selectedEmail?.assignedTo ||
    selectedRow?.assigned_to ||
    selectedRow?.assignedTo ||
    workItem?.assignedTo?.displayName ||
    workItem?.assignedTo ||
    "Unassigned";

  const adoId =
    workItem?.id ||
    selectedRow?.ado_user_story_id ||
    selectedRow?.ado_id ||
    selectedEmail?.ado_user_story_id ||
    selectedEmail?.ado_id;
  const org = workItem?.organization || "DWS10062665";
  const proj = workItem?.project || "ADLS Migration";
  const adoUrl = adoId
    ? `https://dev.azure.com/${encodeURIComponent(org)}/${encodeURIComponent(proj)}/_workitems/edit/${adoId}`
    : null;
  const attachmentIndex = previewableAttachments.findIndex(
    (file) => file.path === previewedPath,
  );
  const isAttachmentPreview = attachmentIndex >= 0;
  const canCycleAttachments =
    isAttachmentPreview && previewableAttachments.length > 1;
  const emailStoragePath = selectedEmail?.email_storage_path;
  const emailFile = useMemo(() => {
    if (!emailStoragePath) {
      return null;
    }
    return {
      name: String(emailStoragePath).split("/").pop(),
      path: emailStoragePath,
      type: "email",
    };
  }, [emailStoragePath]);
  const tabFiles = useMemo(() => {
    const files = [];
    if (emailFile) {
      files.push(emailFile);
    }
    files.push(...attachments);
    return files;
  }, [attachments, emailFile]);
  const tabFileCount = tabFiles.length;
  const verified = isVerifiedEmail(selectedEmail);
  const canEdit = activeTab === "pending" && !verified;
  const canReindex = activeTab === "verified";
  const editing =
    reviewMode && selectedRow?.msg_id === selectedEmail?.msg_id;

  useEffect(() => {
    if (selectedEmail?.email_storage_path) {
      setPreviewedPath(selectedEmail.email_storage_path);
      onPreview?.(selectedEmail.email_storage_path);
    } else {
      setPreviewedPath(null);
    }
  }, [
    onPreview,
    selectedEmail?.email_storage_path,
    selectedEmail?.msg_id,
  ]);

  useEffect(() => {
    setThreadSearch("");
    setAppliedFilters(DEFAULT_THREAD_FILTERS);
    setDraftFilters(DEFAULT_THREAD_FILTERS);
    setFiltersOpen(false);
    setListPage(1);
  }, [thread?.conversation_id]);

  useEffect(() => {
    setListPage(1);
  }, [threadSearch, appliedFilters, sortOrder]);

  useEffect(() => {
    const index = filteredEmails.findIndex(
      (email) => email.msg_id === selectedMsgId,
    );
    if (index < 0) return;
    const pageForSelected = Math.floor(index / THREAD_PAGE_SIZE) + 1;
    setListPage((current) =>
      current === pageForSelected ? current : pageForSelected,
    );
    // Only follow the selected email when the selection or thread changes.
    // Search/filter resets stay on page 1.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMsgId, thread?.conversation_id]);

  useEffect(() => {
    setListPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  useLayoutEffect(() => {
    if (detailRef.current) {
      detailRef.current.scrollTop = 0;
    }
  }, [selectedEmail?.msg_id]);

  useEffect(() => {
    setDetailTab("preview");
  }, [selectedEmail?.msg_id]);

  useLayoutEffect(() => {
    if (!previewScrollNonce) {
      return;
    }
    scrollToVisible(previewFrameRef.current, "smooth");
  }, [previewScrollNonce]);

  const showPreview = (path) => {
    setDetailTab("preview");
    setPreviewedPath(path);
    onPreview?.(path);
    setPreviewScrollNonce((count) => count + 1);
  };

  const showAttachmentAt = (index) => {
    const file = previewableAttachments[index];
    if (!file?.path) {
      return;
    }
    showPreview(file.path);
  };

  const showPreviousAttachment = () => {
    if (!canCycleAttachments) {
      return;
    }
    const previousIndex =
      (attachmentIndex - 1 + previewableAttachments.length) %
      previewableAttachments.length;
    showAttachmentAt(previousIndex);
  };

  const showNextAttachment = () => {
    if (!canCycleAttachments) {
      return;
    }
    const nextIndex = (attachmentIndex + 1) % previewableAttachments.length;
    showAttachmentAt(nextIndex);
  };

  const openAllAttachments = () => {
    onOpenAll?.(tabFiles);
  };

  const downloadAllAttachments = () => {
    tabFiles.forEach((file) => {
      if (file?.path) onDownload?.(file.path, file.name);
    });
  };

  const goToListPage = (nextPage) => {
    const page = Math.min(Math.max(1, nextPage), totalPages);
    setListPage(page);
    const email = filteredEmails[(page - 1) * THREAD_PAGE_SIZE];
    if (email && email.msg_id !== selectedEmail?.msg_id) {
      onSelectEmail(email);
    }
  };

  const detailHeader = (
    <div style={styles.detailHeader}>
      <div style={styles.detailTitleWrap}>
        <div style={styles.mailBadge}>
          <Mail size={18} color="#ffffff" />
        </div>
        <div>
          <div style={styles.detailEmailIndex}>
            Email {selectedNumber} of {totalEmails}
          </div>
          <div style={styles.detailSubject}>
            {selectedEmail?.email_subject || "No subject"}
          </div>
          <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
            From: <strong style={{ color: "#334155" }}>{selectedEmail?.mail_from_address || "—"}</strong>
          </div>
        </div>
      </div>
      {!editing && canEdit ? (
        <button
          type="button"
          style={styles.editBtn}
          onClick={() => onEdit(selectedEmail)}
        >
          <Pencil size={14} />
          Edit metadata
        </button>
      ) : !editing && canReindex ? (
        <button
          type="button"
          style={styles.editBtn}
          onClick={() => onReindex(selectedEmail.msg_id)}
        >
          <RefreshCw size={14} />
          ReIndex
        </button>
      ) : null}
    </div>
  );

  const tabsAndBody = (
    <>
      <div style={styles.tabs}>
        {[
          ["preview", "Preview"],
          [
            "attachments",
            `Attachments (${tabFileCount})`,
          ],
          ["metadata", "Metadata"],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            style={{
              ...styles.tab,
              ...(visibleDetailTab === id ? styles.tabActive : {}),
            }}
            onClick={() => setDetailTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={styles.tabBody}>
        {visibleDetailTab === "preview" && (
          <div
            ref={previewFrameRef}
            className="scrollable"
            style={styles.previewFrame}
          >
            {previewLoading ? (
              <div style={styles.previewStatus}>Loading preview...</div>
            ) : previewError ? (
              <div style={styles.previewStatus}>{previewError}</div>
            ) : previewUrl ? (
              previewKind === "image" ? (
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={styles.previewImage}
                />
              ) : (
                <iframe
                  key={previewUrl}
                  title="Preview"
                  src={previewUrl}
                  style={styles.previewIframe}
                  onLoad={(event) => fitPreviewIframe(event.currentTarget)}
                />
              )
            ) : (
              <div style={styles.previewStatus}>
                {emailFile
                  ? "Preview will appear here."
                  : "Original email file is not available for preview."}
              </div>
            )}
            {canCycleAttachments ? (
              <>
                <button
                  type="button"
                  style={{ ...styles.navArrow, ...styles.navArrowLeft }}
                  onClick={showPreviousAttachment}
                  title="Previous attachment"
                  aria-label="Previous attachment"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  type="button"
                  style={{ ...styles.navArrow, ...styles.navArrowRight }}
                  onClick={showNextAttachment}
                  title="Next attachment"
                  aria-label="Next attachment"
                >
                  <ChevronRight size={20} />
                </button>
                <div style={styles.previewCounter}>
                  {attachmentIndex + 1} / {previewableAttachments.length}
                </div>
              </>
            ) : null}
          </div>
        )}

        {visibleDetailTab === "attachments" && (
          <div style={styles.attachPanel} className="scrollable">
            <div style={styles.attachHeader}>
              <div style={styles.attachTitle}>
                {tabFileCount} {tabFileCount === 1 ? "attachment" : "attachments"}
              </div>
              <div style={styles.attachHeaderRight}>
                {tabFileCount > 0 ? (
                  <div style={styles.attachActions}>
                    <button
                      type="button"
                      style={styles.textLink}
                      onClick={downloadAllAttachments}
                    >
                      <Download size={14} />
                      Download all
                    </button>
                    <button
                      type="button"
                      style={styles.textLink}
                      onClick={openAllAttachments}
                    >
                      <Eye size={14} />
                      Open all
                    </button>
                  </div>
                ) : null}
                <div style={styles.viewToggle} role="group" aria-label="Attachment layout">
                  <button
                    type="button"
                    style={{
                      ...styles.viewToggleBtn,
                      ...(attachLayout === "grid" ? styles.viewToggleBtnActive : {}),
                    }}
                    title="Grid view"
                    aria-pressed={attachLayout === "grid"}
                    onClick={() => setAttachLayout("grid")}
                  >
                    <LayoutGrid size={14} />
                  </button>
                  <button
                    type="button"
                    style={{
                      ...styles.viewToggleBtn,
                      ...(attachLayout === "list" ? styles.viewToggleBtnActive : {}),
                    }}
                    title="List view"
                    aria-pressed={attachLayout === "list"}
                    onClick={() => setAttachLayout("list")}
                  >
                    <List size={14} />
                  </button>
                </div>
              </div>
            </div>
            {tabFileCount === 0 ? (
              <div style={styles.muted}>No attachments</div>
            ) : attachLayout === "grid" ? (
              <div
                style={{
                  ...styles.attachGrid,
                  ...(isNarrow ? styles.attachGridNarrow : {}),
                }}
              >
                {tabFiles.map((file, index) => (
                  <AttachmentGridCard
                    key={`${file.path || file.name}-${index}`}
                    file={file}
                    onPreview={showPreview}
                    previewLoading={previewLoading}
                  />
                ))}
              </div>
            ) : (
              tabFiles.map((file, index) => (
                <FileCard
                  key={`${file.path || file.name}-${index}`}
                  file={file}
                  onPreview={showPreview}
                  onDownload={onDownload}
                  previewLoading={previewLoading}
                />
              ))
            )}
          </div>
        )}

        {visibleDetailTab === "metadata" && (
          <div style={styles.metaPanel} className="scrollable">
            <MetadataCard
              label="Document type"
              value={selectedEmail?.document_type || "UNKNOWN"}
            />
            <MetadataCard
              label="Policy number"
              value={selectedEmail?.policy_number || "N/A"}
            />
            <MetadataCard label="LOB" value={selectedEmail?.lob || "—"} />
            <MetadataCard
              label="Agency no"
              value={selectedEmail?.agency_no || "—"}
            />
            <MetadataCard
              label="Primary insured"
              value={selectedEmail?.primary_insured || "—"}
            />
            <MetadataCard
              label="Entry date"
              value={formatEntryDate(selectedEmail?.created_at)}
            />
            <MetadataCard
              label="Processing status"
              value={verified ? "Verified" : "Pending"}
            />
            {verified ? (
              <>
                <MetadataCard
                  label="Verified by"
                  value={selectedEmail?.verified_by || "—"}
                />
                <MetadataCard
                  label="Verified at"
                  value={formatEntryDate(selectedEmail?.verified_at)}
                />
              </>
            ) : null}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "#f1f5f9", overflow: "hidden" }}>
      {/* Top Header Navigation */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 20px",
          background: "#ffffff",
          borderBottom: "1px solid #e2e8f0",
          flexShrink: 0,
          boxShadow: "0 1px 2px rgba(15,23,42,0.03)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            type="button"
            onClick={onCloseReview || onCollapse}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: 0,
              background: "transparent",
              border: "none",
              color: "#2563eb",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              outline: "none",
            }}
          >
            <ArrowLeft size={15} color="#2563eb" /> Back
          </button>

          <span style={{ color: "#cbd5e1" }}>|</span>

          <span style={{ fontSize: "13px", fontWeight: "700", color: "#0f172a" }}>
            {selectedEmail?.policy_number ? `Policy: ${selectedEmail.policy_number}` : selectedEmail?.email_subject || "Assignment Detail"}
          </span>

          {selectedEmail?.document_type && (
            <span
              style={{
                fontSize: "11px",
                fontWeight: "700",
                padding: "2px 8px",
                borderRadius: "6px",
                background: "#eff6ff",
                color: "#1d4ed8",
                textTransform: "uppercase",
              }}
            >
              {selectedEmail.document_type}
            </span>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* Transfer Queue Button */}
          <button
            type="button"
            onClick={() => {
              setSelectedTargetDept("");
              setTransferError("");
              setTransferSuccess("");
              setShowTransferModal(true);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "5px 12px",
              borderRadius: "7px",
              background: "#fef08a",
              border: "1px solid #eab308",
              color: "#854d0e",
              fontSize: "12px",
              fontWeight: "700",
              cursor: "pointer",
              transition: "all 0.15s ease",
              boxShadow: "0 1px 3px rgba(161,98,7,0.15)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#fde047";
              e.currentTarget.style.borderColor = "#ca8a04";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#fef08a";
              e.currentTarget.style.borderColor = "#eab308";
            }}
            title="Transfer item to another department queue"
          >
            <ArrowRightLeft size={13} color="#854d0e" /> Transfer Queue
          </button>

          {/* View in ADO Button */}
          {adoUrl && (
            <a
              href={adoUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "5px 12px",
                borderRadius: "7px",
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                color: "#1d4ed8",
                fontSize: "12px",
                fontWeight: "600",
                textDecoration: "none",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#dbeafe";
                e.currentTarget.style.borderColor = "#93c5fd";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#eff6ff";
                e.currentTarget.style.borderColor = "#bfdbfe";
              }}
              title="Open work item in Azure DevOps"
            >
              <ExternalLink size={13} /> View in ADO {adoId}
            </a>
          )}

          {/* Assigned To Badge */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "5px 12px",
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: "7px",
              fontSize: "12px",
              color: "#1d4ed8",
              fontWeight: "600",
            }}
            title={`Assigned to: ${assigneeName}`}
          >
            <User size={13} color="#1d4ed8" />
            <span>Assigned to:</span>
            <strong style={{ color: "#1e3a8a", fontWeight: "700" }}>{assigneeName}</strong>
          </div>
        </div>
      </div>

      <div
        style={{
          ...styles.shell,
          flex: 1,
          minHeight: 0,
          borderRadius: 0,
          border: "none",
          borderTop: "1px solid #e2e8f0",
          ...(isNarrow ? styles.shellNarrow : {}),
        }}
      >
        <aside
          style={{
            ...styles.sidebar,
            ...(isNarrow ? styles.sidebarNarrow : {}),
          }}
        >
          <div style={styles.sidebarFill}>
            <div style={styles.sidebarHeader}>
              <div style={styles.headerRow}>
                <div style={styles.sidebarTitle}>Emails in this conversation</div>
                {showThreadFilters ? (
                  <div style={styles.headerActions}>
                    <select
                      value={sortOrder}
                      onChange={(event) => setSortOrder(event.target.value)}
                      style={styles.sortSelect}
                      aria-label="Sort emails"
                    >
                      <option value="newest">Newest first</option>
                      <option value="oldest">Oldest first</option>
                    </select>
                    <button
                      ref={filtersBtnRef}
                      type="button"
                      style={{
                        ...styles.filterBtn,
                        ...(filtersOpen || filtersActive
                          ? styles.filterBtnActive
                          : {}),
                      }}
                      title="Advanced filters"
                      aria-label="Advanced filters"
                      aria-expanded={filtersOpen}
                      aria-haspopup="dialog"
                      onClick={() => {
                        setDraftFilters(appliedFilters);
                        setFiltersOpen((open) => !open);
                      }}
                    >
                      <Filter size={15} />
                    </button>
                    <ThreadAdvancedFilters
                      open={filtersOpen}
                      anchorRef={filtersBtnRef}
                      draft={draftFilters}
                      setDraft={setDraftFilters}
                      onClose={closeFilters}
                      onClear={() => {
                        setDraftFilters(DEFAULT_THREAD_FILTERS);
                        setAppliedFilters(DEFAULT_THREAD_FILTERS);
                        setFiltersOpen(false);
                      }}
                      onApply={() => {
                        setAppliedFilters(draftFilters);
                        setFiltersOpen(false);
                      }}
                    />
                  </div>
                ) : null}
              </div>
              {showThreadSearch ? (
                <div style={styles.sidebarSearchWrap}>
                  <Search size={14} style={styles.threadSearchIcon} />
                  <input
                    type="search"
                    value={threadSearch}
                    onChange={(event) => setThreadSearch(event.target.value)}
                    placeholder="Search subject, sender, or email..."
                    aria-label="Search subject, sender, or email"
                    style={styles.threadSearchInput}
                  />
                </div>
              ) : null}
            </div>
            <div style={styles.emailList} className="scrollable">
              {pagedEmails.length === 0 ? (
                <div style={styles.threadEmpty}>
                  {hasQuery
                    ? "No emails match the current filters."
                    : "No emails in this thread."}
                </div>
              ) : (
                pagedEmails.map((email) => {
                  const selected = email.msg_id === selectedEmail?.msg_id;
                  return (
                    <button
                      key={email.msg_id}
                      type="button"
                      style={{
                        ...styles.emailCard,
                        ...(selected ? styles.emailCardSelected : {}),
                      }}
                      onClick={() => onSelectEmail(email)}
                    >
                      <div style={styles.emailCardBody}>
                        <div style={styles.emailCardTop}>
                          <div style={styles.emailCardTime}>
                            {formatListDate(email.created_at)}
                          </div>
                          <span
                            style={{
                              fontSize: "10.5px",
                              fontWeight: "700",
                              color: "#2563eb",
                              background: "#eff6ff",
                              padding: "1px 6px",
                              borderRadius: "4px",
                              textTransform: "uppercase",
                            }}
                          >
                            {email.document_type || email.documentType || "N/A"}
                          </span>
                        </div>
                        <div style={styles.emailCardSubject}>
                          {email.email_subject || "No subject"}
                        </div>
                        <div style={styles.emailCardFrom}>
                          {email.mail_from_address || "—"}
                        </div>
                        {email.attachments && email.attachments.length > 0 && (
                          <div style={{ marginTop: "6px", display: "flex", alignItems: "center", gap: "4px" }}>
                            <span
                              style={{
                                fontSize: "10px",
                                fontWeight: "600",
                                color: "#475569",
                                background: "#f1f5f9",
                                padding: "1px 6px",
                                borderRadius: "4px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                              }}
                            >
                              <FileText size={10} /> {email.attachments.length} attachment{email.attachments.length === 1 ? "" : "s"}
                            </span>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
            <div style={styles.listFooter}>
              <div style={styles.listFooterCount}>
                {hasQuery
                  ? `${filteredCount} of ${totalEmails} ${totalEmails === 1 ? "email" : "emails"}`
                  : `${totalEmails} ${totalEmails === 1 ? "email" : "emails"} in thread`}
              </div>
              {showPager ? (
                <div style={styles.pager}>
                  <button
                    type="button"
                    style={{
                      ...styles.pagerBtn,
                      ...(safePage <= 1 ? styles.pagerBtnDisabled : {}),
                    }}
                    disabled={safePage <= 1}
                    onClick={() => goToListPage(safePage - 1)}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={14} />
                    Prev
                  </button>
                  <span style={styles.pagerCurrent} aria-current="page">
                    {safePage}
                  </span>
                  <button
                    type="button"
                    style={{
                      ...styles.pagerBtn,
                      ...(safePage >= totalPages ? styles.pagerBtnDisabled : {}),
                    }}
                    disabled={safePage >= totalPages}
                    onClick={() => goToListPage(safePage + 1)}
                    aria-label="Next page"
                  >
                    Next
                    <ChevronRight size={14} />
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </aside>

        <section ref={detailRef} data-thread-detail style={styles.detail}>
          {!selectedEmail ? (
            <div style={styles.emptyDetail}>Select an email in this conversation.</div>
          ) : editing ? (
            <div
              style={{
                ...styles.editLayout,
                ...(isNarrow ? styles.editLayoutNarrow : {}),
              }}
            >
              <div style={styles.editFilesPane}>
                {detailHeader}
                {tabsAndBody}
              </div>
              <div
                style={{
                  ...styles.editReviewPane,
                  ...(isNarrow ? styles.editReviewPaneNarrow : {}),
                }}
              >
                <AssignmentMetadataPanel
                  row={selectedEmail || selectedRow}
                  activeTab={activeTab}
                  onClose={onCloseReview}
                  onPreview={showPreview}
                  previewLoading={previewLoading}
                  onReindex={onReindex}
                  onSaved={handleMetadataSaved}
                  isWorkPool={true}
                  source="VIEW_ASSIGNMENT"
                />
              </div>
            </div>
          ) : (
            <>
              {detailHeader}
              {tabsAndBody}
            </>
          )}
        </section>
      </div>

      {/* TRANSFER QUEUE POPUP MODAL */}
      {showTransferModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
          }}
          onClick={() => setShowTransferModal(false)}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "480px",
              boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.35)",
              overflow: "hidden",
              border: "1px solid #e2e8f0",
              animation: "fadeIn 0.15s ease-out",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ padding: "8px", borderRadius: "10px", background: "#fef3c7", color: "#d97706", display: "flex" }}>
                  <ArrowRightLeft size={18} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>Transfer Queue</h3>
                  <p style={{ margin: "2px 0 0", fontSize: "12.5px", color: "#64748b" }}>Transfer this work item to another department queue</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowTransferModal(false)}
                style={{ background: "transparent", border: "none", cursor: "pointer", padding: "4px", color: "#64748b", display: "flex" }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "20px 24px" }}>
              {transferSuccess ? (
                <div style={{ padding: "12px 16px", borderRadius: "8px", background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46", fontSize: "13px", fontWeight: "600", display: "flex", alignItems: "center", gap: "8px" }}>
                  <CheckCircle2 size={18} color="#059669" />
                  <span>{transferSuccess}</span>
                </div>
              ) : (
                <>
                  {transferError && (
                    <div style={{ marginBottom: "14px", padding: "10px 12px", borderRadius: "8px", background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", fontSize: "12.5px", fontWeight: "600", display: "flex", alignItems: "center", gap: "8px" }}>
                      <AlertCircle size={16} color="#dc2626" />
                      <span>{transferError}</span>
                    </div>
                  )}

                  {/* Current Department Badge */}
                  <div style={{ marginBottom: "16px", padding: "10px 14px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "13px", color: "#475569", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: 600 }}>Current Department:</span>
                    <span style={{ fontWeight: 700, color: "#1d4ed8", background: "#eff6ff", border: "1px solid #bfdbfe", padding: "3px 10px", borderRadius: "6px" }}>
                      {currentDeptName}
                    </span>
                  </div>

                  {/* Target Department Dropdown */}
                  <div style={{ marginBottom: "16px" }}>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: "700", color: "#334155", marginBottom: "6px" }}>
                      Select Target Department <span style={{ color: "#dc2626" }}>*</span>
                    </label>
                    <select
                      value={selectedTargetDept}
                      onChange={(e) => setSelectedTargetDept(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: selectedTargetDept ? "2px solid #2563eb" : "1px solid #cbd5e1",
                        fontSize: "13.5px",
                        fontWeight: "600",
                        color: selectedTargetDept ? "#0f172a" : "#64748b",
                        outline: "none",
                        background: "#ffffff",
                        cursor: "pointer",
                        boxSizing: "border-box",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <option value="">-- Select Target Department --</option>
                      {availableDepartments.map((dept) => {
                        const name = dept.departmentName || dept.department_name || dept.name || dept.departmentCode;
                        return (
                          <option key={dept.departmentId || dept.department_id || name} value={name}>
                            {name}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* Optional Remarks */}
                  <div>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px" }}>
                      Transfer Reason / Remarks (Optional)
                    </label>
                    <textarea
                      rows={2}
                      value={transferRemarks}
                      onChange={(e) => setTransferRemarks(e.target.value)}
                      placeholder="Enter optional notes for queue transfer..."
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        border: "1px solid #cbd5e1",
                        fontSize: "13px",
                        outline: "none",
                        resize: "vertical",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{ padding: "14px 24px", background: "#f8fafc", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                type="button"
                onClick={() => setShowTransferModal(false)}
                disabled={transferring}
                style={{
                  padding: "9px 18px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: "#475569",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExecuteTransfer}
                disabled={!selectedTargetDept || transferring}
                style={{
                  padding: "9px 24px",
                  borderRadius: "8px",
                  border: "none",
                  fontSize: "13px",
                  fontWeight: "700",
                  transition: "all 0.15s ease",
                  background: selectedTargetDept && !transferring ? "#2563eb" : "#e2e8f0",
                  color: selectedTargetDept && !transferring ? "#ffffff" : "#94a3b8",
                  cursor: selectedTargetDept && !transferring ? "pointer" : "not-allowed",
                  boxShadow: selectedTargetDept && !transferring ? "0 2px 8px rgba(37,99,235,0.25)" : "none",
                }}
              >
                {transferring ? "Transferring..." : "Transfer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetadataCard({ label, value }) {
  return (
    <div style={styles.metaCard}>
      <div style={styles.metaCardLabel}>{label}</div>
      <div style={styles.metaCardValue}>{value || "—"}</div>
    </div>
  );
}

function AttachmentGridCard({ file, onPreview, previewLoading }) {
  const kind = fileKind(file);
  const colors = fileIconColors(kind);
  const size = formatFileSize(file.size_bytes);

  return (
    <button
      type="button"
      style={styles.gridCard}
      disabled={previewLoading || !file?.path}
      title={file.name || "Untitled"}
      onClick={() => onPreview?.(file.path)}
    >
      <div style={{ ...styles.gridCardIcon, ...colors }}>
        {kind === "image" ? <ImageIcon size={22} /> : <FileText size={22} />}
      </div>
      <div style={styles.gridCardName}>{file.name || "Untitled"}</div>
      <div style={styles.gridCardSize}>{size || "—"}</div>
    </button>
  );
}

function FileCard({ file, label, onPreview, onDownload, previewLoading }) {
  const kind = fileKind(file);
  const size = formatFileSize(file.size_bytes);
  const kindLabel =
    label ||
    (kind === "pdf" ? "PDF" : kind === "image" ? "Image" : "File");

  return (
    <div style={styles.fileCard}>
      <div
        style={{
          ...styles.fileIcon,
          ...fileIconColors(kind),
        }}
      >
        {kind === "image" ? <ImageIcon size={16} /> : <FileText size={16} />}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={styles.fileName}>{file.name || "Untitled"}</div>
        <div style={styles.fileMeta}>
          {kindLabel}
          {size ? ` • ${size}` : ""}
        </div>
      </div>
      <button
        type="button"
        style={styles.iconBtn}
        title="Download"
        onClick={() => onDownload?.(file.path, file.name)}
      >
        <Download size={14} />
      </button>
      <button
        type="button"
        style={styles.iconBtn}
        title="Preview"
        disabled={previewLoading}
        onClick={() => onPreview?.(file.path)}
      >
        <Eye size={14} />
      </button>
    </div>
  );
}

const styles = {
  shell: {
    display: "flex",
    alignItems: "stretch",
    minHeight: 0,
    background: "#ffffff",
    overflow: "hidden",
  },
  shellNarrow: {
    flexDirection: "column",
    minHeight: 0,
  },
  sidebar: {
    width: 360,
    minWidth: 280,
    flex: "0 0 360px",
    alignSelf: "stretch",
    position: "relative",
    overflow: "hidden",
    borderRight: "1px solid #e2e8f0",
    background: "#fff",
  },
  sidebarNarrow: {
    width: "100%",
    minWidth: 0,
    flex: "0 0 360px",
    height: 360,
    maxHeight: 360,
    borderRight: 0,
    borderBottom: "1px solid #e2e8f0",
  },
  sidebarFill: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
  },
  sidebarHeader: {
    padding: "12px 12px 10px",
    borderBottom: "1px solid #e2e8f0",
    flexShrink: 0,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },
  backBtn: {
    width: 28,
    height: 28,
    border: 0,
    background: "transparent",
    color: "#334155",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
    flexShrink: 0,
    padding: 0,
  },
  sidebarTitle: {
    fontWeight: 600,
    fontSize: 13,
    lineHeight: 1.3,
    color: "#0f172a",
    flex: 1,
    minWidth: 0,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },
  sortSelect: {
    height: 32,
    maxWidth: 128,
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "0 8px",
    background: "#fff",
    color: "#334155",
    fontSize: 12,
    fontWeight: 500,
  },
  filterBtn: {
    width: 32,
    height: 32,
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    background: "#fff",
    color: "#475569",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  },
  filterBtnActive: {
    border: "1px solid #2563eb",
    color: "#1d4ed8",
    background: "#eff6ff",
  },
  sidebarSearchWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    width: "100%",
    marginTop: 10,
  },
  threadSearchWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    width: "100%",
  },
  threadSearchIcon: {
    position: "absolute",
    left: 10,
    color: "#94a3b8",
    pointerEvents: "none",
  },
  threadSearchInput: {
    width: "100%",
    height: 32,
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "0 8px 0 30px",
    background: "#fff",
    fontSize: 12,
    color: "#0f172a",
    boxSizing: "border-box",
  },
  threadEmpty: {
    fontSize: 13,
    color: "#64748b",
    padding: "16px 8px",
    textAlign: "center",
  },
  filterChipRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  filterChip: {
    height: 32,
    padding: "0 12px",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    background: "#fff",
    color: "#0f172a",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  filterChipActive: {
    background: "#2563eb",
    color: "#ffffff",
    border: "1px solid #2563eb",
    fontWeight: 600,
  },
  filtersPopover: {
    position: "fixed",
    zIndex: 80,
    width: 320,
    maxHeight: 420,
    overflow: "auto",
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.16)",
    padding: 16,
    boxSizing: "border-box",
  },
  filtersTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#0f172a",
    marginBottom: 12,
  },
  filtersSection: {
    marginBottom: 14,
  },
  filtersSectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.4,
    color: "#64748b",
    marginBottom: 8,
  },
  filtersRadio: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: "#334155",
    marginBottom: 6,
    cursor: "pointer",
  },
  filtersDateRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8,
    marginTop: 8,
  },
  filtersDateInput: {
    height: 32,
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "0 8px",
    fontSize: 12,
  },
  filtersFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 8,
    paddingTop: 12,
    borderTop: "1px solid #e2e8f0",
    position: "sticky",
    bottom: 0,
    background: "#fff",
  },
  filtersClear: {
    border: 0,
    background: "transparent",
    color: "#475569",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    padding: 0,
  },
  filtersApply: {
    height: 32,
    padding: "0 14px",
    border: 0,
    borderRadius: 8,
    background: "#2563eb",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  emailList: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    padding: 10,
    background: "#f8fafc",
  },
  emailCard: {
    width: "100%",
    display: "block",
    textAlign: "left",
    boxSizing: "border-box",
    border: "1px solid #e2e8f0",
    background: "#fff",
    borderRadius: 10,
    padding: "10px 12px",
    cursor: "pointer",
    marginBottom: 8,
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    transition: "all 0.15s ease",
  },
  emailCardSelected: {
    background: "linear-gradient(to right, #eff6ff, #ffffff)",
    borderColor: "#bfdbfe",
    borderLeft: "4px solid #2563eb",
    boxShadow: "0 2px 8px rgba(37, 99, 235, 0.08)",
  },
  emailCardBody: {
    minWidth: 0,
  },
  emailCardTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 4,
  },
  emailCardTime: {
    color: "#64748b",
    fontSize: 11,
    lineHeight: 1.3,
    whiteSpace: "nowrap",
  },
  emailCardSubject: {
    fontWeight: 700,
    color: "#0f172a",
    fontSize: 13,
    lineHeight: 1.35,
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  emailCardFrom: {
    color: "#2563eb",
    fontSize: 12,
    lineHeight: 1.35,
    marginTop: 4,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  listFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    minHeight: 44,
    padding: "8px 12px",
    borderTop: "1px solid #e2e8f0",
    background: "#fff",
    flexShrink: 0,
  },
  listFooterCount: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  pager: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },
  pagerBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    height: 28,
    padding: "0 6px",
    border: 0,
    background: "transparent",
    color: "#334155",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  pagerBtnDisabled: {
    color: "#cbd5e1",
    cursor: "default",
  },
  pagerCurrent: {
    minWidth: 28,
    height: 28,
    padding: "0 8px",
    borderRadius: 6,
    background: "#2563eb",
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  detail: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    height: "100%",
    minHeight: 0,
    background: "#f8fafc",
  },
  editLayout: {
    display: "flex",
    alignItems: "stretch",
    minWidth: 0,
    flex: 1,
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
  },
  editLayoutNarrow: {
    flexDirection: "column",
    overflow: "auto",
  },
  editFilesPane: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
  },
  editReviewPane: {
    width: 400,
    minWidth: 360,
    maxWidth: 440,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    background: "#fff",
    borderLeft: "1px solid #e2e8f0",
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
  },
  editReviewPaneNarrow: {
    width: "100%",
    minWidth: 0,
    maxWidth: "none",
    borderLeft: 0,
    borderTop: "1px solid #e2e8f0",
  },
  emptyDetail: {
    padding: 32,
    color: "#64748b",
  },
  detailHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    padding: 20,
    background: "#fff",
    borderBottom: "1px solid #e2e8f0",
  },
  detailTitleWrap: {
    display: "flex",
    gap: 12,
    minWidth: 0,
  },
  mailBadge: {
    width: 38,
    height: 38,
    borderRadius: 10,
    background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: "0 2px 8px rgba(37,99,235,0.25)",
  },
  detailSubject: {
    fontSize: 15,
    fontWeight: 700,
    color: "#0f172a",
    lineHeight: 1.35,
  },
  detailEmailIndex: {
    fontSize: 12,
    fontWeight: 600,
    color: "#64748b",
    marginBottom: 4,
  },
  editBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    height: 38,
    padding: "0 14px",
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    background: "#fff",
    color: "#0f172a",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  infoBox: {
    margin: 0,
    padding: "10px 14px",
    background: "#f1f5f9",
    borderRadius: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  infoRow: {
    display: "flex",
    gap: 16,
    fontSize: 13,
  },
  infoLabel: {
    width: 110,
    flexShrink: 0,
    color: "#64748b",
    fontWeight: 600,
  },
  infoValue: {
    color: "#0f172a",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
    overflowWrap: "anywhere",
  },
  copyBtn: {
    border: 0,
    background: "transparent",
    color: "#64748b",
    cursor: "pointer",
    padding: 0,
    display: "inline-flex",
  },
  tabs: {
    display: "flex",
    gap: "8px",
    padding: "10px 20px",
    borderBottom: "1px solid #e2e8f0",
    background: "#ffffff",
    alignItems: "center",
  },
  tab: {
    border: "none",
    background: "transparent",
    padding: "7px 14px",
    borderRadius: "8px",
    color: "#64748b",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  tabActive: {
    color: "#2563eb",
    background: "rgba(37, 99, 235, 0.08)",
    fontWeight: "700",
  },
  tabBody: {
    padding: 0,
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  previewFrame: {
    position: "relative",
    height: "100%",
    minHeight: 480,
    background: "#ffffff",
    border: "none",
    borderRadius: 0,
    overflow: "auto",
    display: "block",
  },
  navArrow: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: 36,
    height: 36,
    border: 0,
    borderRadius: "50%",
    background: "rgba(15, 23, 42, 0.75)",
    color: "#fff",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    padding: 0,
  },
  navArrowLeft: {
    left: 12,
  },
  navArrowRight: {
    right: 12,
  },
  previewCounter: {
    position: "absolute",
    bottom: 12,
    left: "50%",
    transform: "translateX(-50%)",
    background: "rgba(15, 23, 42, 0.75)",
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    padding: "4px 10px",
    borderRadius: 999,
    zIndex: 2,
    whiteSpace: "nowrap",
  },
  previewIframe: {
    width: "100%",
    minHeight: "100%",
    height: "100%",
    border: "none",
    background: "#fff",
    display: "block",
  },
  previewImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    objectPosition: "center",
    display: "block",
    background: "#0f172a",
  },
  previewStatus: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#64748b",
    fontSize: 14,
    padding: 24,
    textAlign: "center",
  },
  muted: {
    color: "#64748b",
    fontSize: 13,
    marginBottom: 16,
  },
  attachHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    margin: "0 0 16px",
    position: "sticky",
    top: 0,
    zIndex: 1,
    background: "#fff",
    paddingBottom: 4,
  },
  attachTitle: {
    fontWeight: 700,
    color: "#334155",
    fontSize: 14,
  },
  attachHeaderRight: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    flexShrink: 0,
  },
  attachActions: {
    display: "flex",
    gap: 16,
  },
  attachPanel: {
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 16,
    background: "#fff",
    height: 520,
    overflow: "auto",
    boxSizing: "border-box",
  },
  attachGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
  },
  attachGridNarrow: {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  viewToggle: {
    display: "inline-flex",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    overflow: "hidden",
    background: "#fff",
  },
  viewToggleBtn: {
    width: 32,
    height: 32,
    border: 0,
    background: "transparent",
    color: "#64748b",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  },
  viewToggleBtnActive: {
    background: "#eff6ff",
    color: "#2563eb",
  },
  gridCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 8,
    padding: "20px 12px 16px",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    background: "#fff",
    cursor: "pointer",
    minWidth: 0,
    font: "inherit",
  },
  gridCardIcon: {
    width: 48,
    height: 48,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  gridCardName: {
    fontSize: 13,
    fontWeight: 600,
    color: "#334155",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    width: "100%",
  },
  gridCardSize: {
    fontSize: 12,
    color: "#94a3b8",
  },
  metaPanel: {
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 12,
    background: "#f8fafc",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    height: "auto",
    maxHeight: 520,
    overflow: "auto",
    boxSizing: "border-box",
  },
  metaCard: {
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "10px 12px",
    background: "#fff",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
  },
  metaCardLabel: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.5,
    color: "#94a3b8",
    textTransform: "uppercase",
    marginBottom: 4,
  },
  metaCardValue: {
    fontSize: 13,
    fontWeight: 600,
    color: "#0f172a",
    overflowWrap: "anywhere",
  },
  textLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: 0,
    background: "transparent",
    color: "#2563eb",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 13,
  },
  fileCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 12,
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    marginBottom: 10,
  },
  fileIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  fileName: {
    fontWeight: 600,
    color: "#0f172a",
    fontSize: 13,
    overflowWrap: "anywhere",
  },
  fileMeta: {
    color: "#64748b",
    fontSize: 12,
    marginTop: 2,
  },
  iconBtn: {
    width: 32,
    height: 32,
    border: "1px solid #e2e8f0",
    background: "#fff",
    borderRadius: 8,
    color: "#334155",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pill: {
    display: "inline-block",
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: 999,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  pillPending: {
    background: "#fff7ed",
    color: "#c2410c",
    border: "1px solid #fdba74",
  },
  pillVerified: {
    background: "#f0fdf4",
    color: "#15803d",
    border: "1px solid #86efac",
  },
};
